// src/pages/ProductGroups.js
import React, { useEffect, useState, useCallback } from "react";
import { PencilIcon, TrashIcon, PlusIcon } from "lucide-react";
import { Dialog } from "@headlessui/react";
import { Boxes } from "lucide-react";
import api from "../services/api";

const ProductGroups = ({ role }) => {
  const [groups, setGroups] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [editingGroup, setEditingGroup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const token = localStorage.getItem("token");

  const fetchGroups = useCallback(async () => {
    try {
      const res = await api.get(`/products/group`, {
        headers: { authorization: token },
      });
      setGroups(res.data);
    } catch (err) {
      console.error("Gagal mengambil data product group", err);
    }
  }, [token]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleSubmit = async () => {
    try {
      if (editingGroup) {
        await api.put(
          `/products/group/${editingGroup.id}`,
          { name, description },
          { headers: { authorization: token } }
        );
        alert("Product group berhasil diperbarui");
      } else {
        await api.post(
          `/products/group`,
          { name, description },
          { headers: { authorization: token } }
        );
        alert("Product group berhasil ditambahkan");
      }
      setName("");
      setDescription("");
      setEditingGroup(null);
      setIsModalOpen(false);
      fetchGroups();
    } catch (err) {
      console.error("Gagal mengirim data", err);
      alert("Terjadi kesalahan saat mengirim data");
    }
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setName(group.name);
    setDescription(group.description);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin ingin menghapus product group ini?")) return;
    try {
      await api.delete(`/products/group/${id}`, {
        headers: { authorization: token },
      });
      alert("Product group berhasil dihapus");
      fetchGroups();
    } catch (err) {
      console.error("Gagal menghapus product group", err);
      alert("Terjadi kesalahan saat menghapus");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingGroup(null);
    setName("");
    setDescription("");
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Boxes /> Product Groups
        </h1>
      </div>
      <div className="flex items-center justify-between mb-6">
        {role === "billing" && (
          <button
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 shadow-md"
            onClick={() => setIsModalOpen(true)}
          >
            <PlusIcon size={18} /> Tambah Product Group
          </button>
        )}
      </div>

      {/* Modal */}
      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        className="fixed z-50 inset-0 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Panel className="bg-white dark:bg-gray-600 p-6 rounded-2xl shadow-xl w-full max-w-md">
            <Dialog.Title className="text-xl font-bold mb-4 text-center text-black dark:text-white">
              {editingGroup ? "Edit Product Group" : "Tambah Product Group"}
            </Dialog.Title>

            <div className="space-y-4">
              <input
                className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                placeholder="Nama"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <textarea
                className="w-full text-black dark:text-white border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                placeholder="Deskripsi"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleSubmit}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl"
              >
                {editingGroup ? "Update" : "Tambah"}
              </button>
              <button
                onClick={closeModal}
                className="bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 text-black dark:text-white px-4 py-2 rounded-xl"
              >
                Batal
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl shadow mt-6">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
              <th className="px-6 py-3 text-left font-semibold">Nama</th>
              <th className="px-6 py-3 text-left font-semibold">Deskripsi</th>
              {role === "billing" && (
                <th className="px-6 py-3 text-center font-semibold">Aksi</th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800">
            {groups.map((group) => (
              <tr
                key={group.id}
                className="border-b hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <td className="px-6 py-4">{group.name}</td>
                <td className="px-6 py-4">{group.description}</td>
                {role === "billing" && (
                  <td className="px-6 py-4 flex justify-center gap-3">
                    <button
                      onClick={() => handleEdit(group)}
                      className="flex items-center gap-1 px-3 py-2 text-blue-600 hover:text-blue-800 rounded-md border border-blue-600 hover:border-blue-800 text-sm transition"
                    >
                      <PencilIcon size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(group.id)}
                      className="flex items-center gap-1 px-3 py-2 text-red-600 hover:text-red-800 rounded-md border border-red-600 hover:border-red-800 text-sm transition"
                    >
                      <TrashIcon size={16} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductGroups;
